import { ItemsSummaryWrapper } from "@components/formGroup";
import { getWorkDate } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { getSimpleBoolSelectItems, IGetValueArgs, not, TGetValueFn } from "@components/smart/FieldInfo";
import { BirthNumberFieldDef, getAddressFields, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { createPath } from "@odata/BindingContext";
import { getPropertyTranslationSync } from "@odata/FieldInfo.utils";
import {
    EntitySetName,
    EntityTypeName,
    GovernmentInstitutionEntity,
    PrEldpCompanyEntity,
    PrEldpEmployeeEntity,
    PrEldpEntity,
    PrEldpItemEntity,
    PrEldpItemTypeEntity,
    PrEldpTypeEntity,
    PrEmployeeEntity,
    PrEntityEmployeePermanentAddressEntity
} from "@odata/GeneratedEntityTypes";
import { PrEldpItemTypeCode, PrEldpTypeCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import {
    getDateInEldpYear,
    getEldpAddYearOptions,
    getEldpCurrencyPath,
    TEldpFormStorage
} from "@pages/payroll/Eldp/Eldp.utils";
import EldpFormView from "@pages/payroll/Eldp/EldpFormView";
import EldpItemsSummary from "@pages/payroll/Eldp/EldpItemsSummary";
import SmartMonthCheckboxSelector from "@pages/payroll/Eldp/SmartMonthCheckboxSelector";
import {
    getEmployeeFilterDef,
    getEmployeeSelectDef,
    getEmployeeTableColumnDefs
} from "@pages/payroll/employee/Employee.utils";
import { getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import { isNewEntity } from "@utils/definition.utils";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER } from "../../../constants";
import { BasicInputSizes, FastEntryInputSizes, FieldType, GroupedField, Sort, TextAlign } from "../../../enums";
import { TValue } from "../../../global.types";
import { getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { IDefinition, IGetDefinition } from "../../PageUtils";

const yearFormatter = (val: TValue): string => {
    if (!val) {
        return DASH_CHARACTER;
    }
    return val.toString();
};

const getFullMetadataLabel: TGetValueFn<string> = ({ bindingContext }) => {
    return getPropertyTranslationSync(bindingContext);
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrEldp}Table`,
        additionalProperties: [/*{
            id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Attendance, PrAttendanceEntity.StatusCode)
        }*/],
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            isValueHelp: true,
            defaultFilters: [
                PrEldpEntity.EldpType,
                createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee),

            ],
            filterDefinition: {
                ...getEmployeeFilterDef(createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee)),
                ...withDisplayName(PrEldpEntity.EldpType),
                [PrEldpEntity.DateEmploymentStart]: {},
            }
        }],
        columns: [
            createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee),
            createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee, PrEmployeeEntity.NumberOurs),
            PrEldpEntity.EldpType,
        ],
        columnDefinition: {
            [PrEldpEntity.Year]: {
                formatter: yearFormatter
            },
            ...getEmployeeTableColumnDefs(createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee)),
            ...withDisplayName(PrEldpEntity.EldpType),
            [PrEldpEntity.DateEmploymentStart]: {},
        },
        title: i18next.t("Eldp:Table.Title"),
        initialSortBy: [{ id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee), sort: Sort.Desc }]
    };

    const EldpTypeFormatter = (val: TValue): string => {
        if (!val) {
            return DASH_CHARACTER;
        }
        return `${val} - ${getEnumDisplayValue(EntityTypeName.PrEldpType, val?.toString())}`;
    };

    const form: IFormDef = {
        id: `${EntityTypeName.PrEldp}Form`,
        title: i18next.t("Eldp:Form.Title"),
        translationFiles: getDefinitions.translationFiles,
        summary: [
            /*, {
                id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.WorkedIncludingHolidays),
                label: i18next.t("Payslip:Form.Worked"),
                formatter: workedHoursFormatter
            }*/
        ],
        customHeaderActions: [
            // {
            //     id: PayslipFormAction.ConnectLiabilities,
            //     label: i18next.t("Payslip:Form.ConnectWithLiabilities"),
            //     iconName: "ConnectLiability"
            // }
        ],
        additionalProperties: [],
        formControl: EldpFormView,
        fieldDefinition: {
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee)]: getEmployeeSelectDef(),
            [PrEldpEntity.Year]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.S,
                defaultValue: (args: IGetValueArgs) => {
                    const year = (args.storage as TEldpFormStorage).getCustomData()?.Year;
                    return year ?? (getUtcDayjs(getWorkDate()).get("year") - 1);
                },
                fieldSettings: {
                    itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem[]> => {
                        // todo: which options to show?
                        const storage = args.storage as TEldpFormStorage;
                        return getEldpAddYearOptions(storage.getCustomData()?.Year ?? 0);
                    }
                }
            },
            [PrEldpEntity.DateIssued]: {
                defaultValue: () => getWorkDate()
            },
            [PrEldpEntity.DateEmploymentStart]: {
                label: i18next.t("Eldp:Form.DateEmploymentStart"),
            },
            [PrEldpEntity.EldpType]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.XXL,
                defaultValue: PrEldpTypeCode.CurrentEmployment,
                fieldSettings: {
                    displayName: PrEldpTypeEntity.Name,
                    transformFetchedItems: (items: ISelectItem[]): ISelectItem[] => {
                        return items
                                .sort((a, b) => parseInt(a.id as string) - parseInt(b.id as string))
                                .map((item) => ({
                                    ...item,
                                    label: EldpTypeFormatter(item.id)
                                }));
                    }
                },
                formatter: EldpTypeFormatter,
            },
            [PrEldpEntity.SocialAdministration]: {
                type: FieldType.ComboBox,
                isReadOnly: true,
                fieldSettings: {
                    displayName: GovernmentInstitutionEntity.ShortName,
                    shouldDisplayAdditionalColumns: true,
                },
                columns: [
                    { id: GovernmentInstitutionEntity.ShortName },
                    { id: GovernmentInstitutionEntity.OpenDataCode }
                ],
                additionalProperties: [
                    { id: GovernmentInstitutionEntity.OpenDataCode }
                ]
            },
            /* ------- Employee section ------- */
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.TitleBeforeName)]: {
                width: BasicInputSizes.XS,
                label: i18next.t("Eldp:Form.TitleBeforeName"),
                groupedField: GroupedField.MultiStart,
                textAlign: TextAlign.Right
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.FirstName)]: {
                isRequired: true
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.LastName)]: {
                isRequired: true
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.TitleAfterName)]: {
                width: BasicInputSizes.XS,
                label: i18next.t("Eldp:Form.TitleAfterName"),
                groupedField: GroupedField.MultiEnd,
                textAlign: TextAlign.Left
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.BirthLastName)]: {
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PersonalIdentificationNumber)]: {
                ...BirthNumberFieldDef,
                isReadOnly: true,
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.DateOfBirth)]: {
                isReadOnly: true,
            },
            ...getAddressFields(createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PermanentAddress)),
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PostOffice)]: {
            },
            [createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PlaceOfBirth)]: {
            },
            /* ------- ELDP line items ------- */
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.ItemType)]: {
                type: FieldType.ComboBox,
                width: FastEntryInputSizes.L,
                defaultValue: PrEldpItemTypeCode.CSSZ_1,
                fieldSettings: {
                    displayName: PrEldpItemTypeEntity.MpsvCode,
                    shouldDisplayAdditionalColumns: true,
                    preloadItems: true,
                },
                columns: [
                    { id: PrEldpItemTypeEntity.MpsvCode },
                    { id: PrEldpItemTypeEntity.Name }
                ],
                additionalProperties: [
                    { id: PrEldpItemTypeEntity.Name }
                ]
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.IsSmallScaleEmployment)]: {
                type: FieldType.ComboBox,
                label: i18next.t("Eldp:Form.IsSmallScaleEmployment"),
                tooltip: getFullMetadataLabel,
                width: FastEntryInputSizes.XXS,
                defaultValue: false,
                fieldSettings: {
                    items: getSimpleBoolSelectItems()
                }
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.DateValidFrom)]: {
                defaultValue: getDateInEldpYear("startOf"),
                fieldSettings: {
                    minDate: getDateInEldpYear("startOf"),
                    maxDate: getDateInEldpYear("endOf")
                },
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.DateValidTo)]: {
                defaultValue: getDateInEldpYear("endOf"),
                fieldSettings: {
                    minDate: getDateInEldpYear("startOf"),
                    maxDate: getDateInEldpYear("endOf")
                },
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.DaysInInterval)]: {
                width: FastEntryInputSizes.XXS,
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.ExcludedMonths)]: {
                type: FieldType.Custom,
                defaultValue: "0".repeat(13),
                render: (args) => {
                    // custom component for excluded months
                    return (<SmartMonthCheckboxSelector {...args.props} {...args.events} />);
                },
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.DaysExcluded)]: {
                label: i18next.t("Eldp:Form.DaysExcluded"),
                tooltip: getFullMetadataLabel,
                width: FastEntryInputSizes.XXS,
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.CalculationBasis)]: {
                ...getPayrollCurrencyFieldDef(getEldpCurrencyPath())
            },
            [createPath(PrEldpEntity.Items, PrEldpItemEntity.DaysDeducted)]: {
                label: i18next.t("Eldp:Form.DaysDeducted"),
                tooltip: getFullMetadataLabel,
                width: FastEntryInputSizes.XXS,
            },
            /* ------- ELDP Company ------- */
            [createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.LegalName)]: {
                isReadOnly: true,
            },
            [createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.LegalNumber)]: {
                isReadOnly: true,
            },
            [createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.SymbolVariable)]: {
                isReadOnly: not(isNewEntity),
            },
        },
        groups: [{
            id: "Base",
            rows: [
                [{ id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee) }],
                    [
                        { id: PrEldpEntity.Year },
                        { id: PrEldpEntity.DateIssued },
                        { id: PrEldpEntity.EldpType },
                        { id: PrEldpEntity.SocialAdministration },
                    ],
            ]
        }, {
            id: "Employee",
            title: i18next.t("Eldp:Form.Employee"),
            rows: [[
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.TitleBeforeName) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.FirstName) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.LastName) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.TitleAfterName) }
            ], [
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.BirthLastName) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PersonalIdentificationNumber) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.DateOfBirth) },
            ], [
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PermanentAddress, PrEntityEmployeePermanentAddressEntity.Street) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PermanentAddress, PrEntityEmployeePermanentAddressEntity.City) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PermanentAddress, PrEntityEmployeePermanentAddressEntity.PostalCode) },
            ], [
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PermanentAddress, PrEntityEmployeePermanentAddressEntity.Country) },
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PostOffice) }
            ], [
                { id: createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.PlaceOfBirth) },
            ]]
        }, {
            id: "Items",
            title: i18next.t("Eldp:Form.YearInsurance"),
            lineItems: {
                collection: PrEldpEntity.Items,
                columns: [
                    { id: PrEldpItemEntity.ItemType },
                    { id: PrEldpItemEntity.IsSmallScaleEmployment },
                    { id: PrEldpItemEntity.DateValidFrom },
                    { id: PrEldpItemEntity.DateValidTo },
                    { id: PrEldpItemEntity.DaysInInterval },
                    { id: PrEldpItemEntity.ExcludedMonths },
                    { id: PrEldpItemEntity.DaysExcluded },
                    { id: PrEldpItemEntity.CalculationBasis },
                    { id: PrEldpItemEntity.DaysDeducted },
                ],
                disableVirtualization: true,
                order: PrEldpItemEntity.DateCreated,
                useLabelWrapping: false,
                canReorder: false,
                isItemCloneable: false,
                canAdd: true,
                isItemRemovable: true,
                itemsSummaryRenderer: (args) => {
                    return (
                            <ItemsSummaryWrapper>
                                <EldpItemsSummary storage={args.storage as TEldpFormStorage}/>
                            </ItemsSummaryWrapper>
                    );
                }
            }
        }, {
            id: "Company",
            title: i18next.t("Eldp:Form.Company"),
            rows: [[
                { id: createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.LegalName) },
                { id: createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.LegalNumber) },
                { id: createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.SymbolVariable) },
                { id: PrEldpEntity.DateEmploymentStart },
            ]],
        }]
    };

    return {
        entitySet: EntitySetName.PrEldps,
        table,
        form
    };
};

getDefinitions.translationFiles = [
    "Eldp", "Common", "Employee",
    // selection tableViews
    getEnumNameSpaceName(EntityTypeName.PrEldpType), getEnumNameSpaceName(EntityTypeName.PrEldpItemType),
    getEnumNameSpaceName(EntityTypeName.GovernmentInstitution)
];
setDefByEntityType(EntityTypeName.PrEldp, getDefinitions);